import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Gavel, Check, Repeat } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { getNotifications, markNotificationsRead } from '../lib/api';

const NOTIF_ICONS = { accepted: Check, settled: Repeat };
const iconFor = (t) => NOTIF_ICONS[t] || Gavel;

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const d = await getNotifications();
      setItems(d.items || []);
      setUnread(d.unread || 0);
    } catch (e) { console.debug('Notifications unavailable (not signed in?)', e); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const onOpen = async (open) => {
    if (open && unread > 0) {
      try { await markNotificationsRead(); } catch (e) { console.error('Failed to mark notifications read', e); }
      setUnread(0);
    }
  };

  return (
    <Popover onOpenChange={onOpen}>
      <PopoverTrigger className="relative grid h-9 w-9 place-items-center rounded-full border border-white/15 text-farmway-cream/80 outline-none transition-colors hover:border-farmway-gold/50 hover:text-farmway-cream">
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 grid h-4 min-w-[16px] place-items-center rounded-full bg-farmway-gold px-1 text-[10px] font-bold text-farmway-green">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 border-white/10 bg-farmway-green-dark p-0 text-farmway-cream">
        <div className="border-b border-white/10 px-4 py-3">
          <p className="font-display text-sm font-bold uppercase tracking-wider">Notifications</p>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-farmway-cream/50">You&apos;re all caught up.</p>
          ) : (
            items.map((n) => {
              const Icon = iconFor(n.type);
              return (
                <div key={n.id} className={`flex items-start gap-3 border-b border-white/5 px-4 py-3 ${n.read ? '' : 'bg-white/5'}`}>
                  <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-farmway-gold/15 text-farmway-gold">
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-farmway-cream">{n.title}</p>
                    <p className="text-xs text-farmway-cream/60">{n.body}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>
        <Link to="/notifications" className="block border-t border-white/10 px-4 py-3 text-center font-display text-xs font-bold uppercase tracking-wider text-farmway-gold hover:bg-white/5">
          View all
        </Link>
      </PopoverContent>
    </Popover>
  );
}
